/**
 * Axios mock interceptor for the guided tour demo mode.
 * Installs a request interceptor that overrides the adapter for specific API
 * endpoints, returning mock data without making real HTTP requests.
 * Required for the SelectionModal which fetches data on open.
 */

import axios from 'axios';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import {
  MOCK_FLIGHT_ACTIVE,
  MOCK_FLIGHT_ENDED,
  MOCK_SEPARATORS,
  MOCK_MESSAGES_ACTIVE,
  MOCK_MESSAGES_ENDED,
  MOCK_PREDICTION_NORMAL,
  MOCK_PREDICTION_INSTANT,
  MOCK_TEAMS,
} from './mockTourData';

let interceptorId: number | null = null;

/** Returns mock response data for a given request config, or null if not mocked. */
function resolveMock(config: InternalAxiosRequestConfig): unknown | null {
  const url = config.url ?? '';
  const params = (config.params ?? {}) as Record<string, string>;

  // GET /imeis?type=separator
  if (url === '/imeis' && params['type'] === 'separator') {
    return { imeis: MOCK_SEPARATORS };
  }
  // GET /imeis/101/flights
  if (url === '/imeis/101/flights') {
    return { flights: [MOCK_FLIGHT_ACTIVE] };
  }
  // GET /imeis/102/flights
  if (url === '/imeis/102/flights') {
    return { flights: [MOCK_FLIGHT_ENDED] };
  }
  // GET /flights/1001
  if (url === '/flights/1001') {
    return { flight: MOCK_FLIGHT_ACTIVE };
  }
  // GET /flights/1002
  if (url === '/flights/1002') {
    return { flight: MOCK_FLIGHT_ENDED };
  }
  // GET /flights/1001/separator-messages
  if (url === '/flights/1001/separator-messages') {
    return { separator_messages: MOCK_MESSAGES_ACTIVE };
  }
  // GET /flights/1002/separator-messages
  if (url === '/flights/1002/separator-messages') {
    return { separator_messages: MOCK_MESSAGES_ENDED };
  }
  // GET /flights/1001/team-positions
  if (url === '/flights/1001/team-positions') {
    return { team: MOCK_TEAMS };
  }
  // GET /flights/1002/team-positions
  if (url === '/flights/1002/team-positions') {
    return { team: [] };
  }
  // GET /flights/1001/predictions or /flights/1002/predictions
  if (url === '/flights/1001/predictions') {
    const type = params['type'];
    if (type === 'normal') return MOCK_PREDICTION_NORMAL;
    if (type === 'instant') return MOCK_PREDICTION_INSTANT;
  }
  if (url === '/flights/1002/predictions') {
    const type = params['type'];
    if (type === 'normal') return MOCK_PREDICTION_NORMAL;
    if (type === 'instant') return MOCK_PREDICTION_INSTANT;
  }
  // POST /users/tetris-score — silently accept
  if (url === '/users/tetris-score') {
    return {};
  }

  return null;
}

/**
 * Installs the tour mock HTTP interceptor.
 * Call before the SelectionModal or any API-fetching component renders in demo mode.
 */
export function setupTourMockHttp(): void {
  if (interceptorId !== null) return;

  interceptorId = axios.interceptors.request.use((config) => {
    const mockData = resolveMock(config);
    if (mockData !== null) {
      config.adapter = (): Promise<AxiosResponse> =>
        Promise.resolve({
          data: mockData,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: null,
        } as AxiosResponse);
    }
    return config;
  });
}

/**
 * Removes the tour mock HTTP interceptor, restoring normal axios behaviour.
 */
export function teardownTourMockHttp(): void {
  if (interceptorId !== null) {
    axios.interceptors.request.eject(interceptorId);
    interceptorId = null;
  }
}
